import React, { useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonList, IonFab, IonFabButton, IonIcon, IonSpinner,
  IonInfiniteScroll, IonInfiniteScrollContent, useIonAlert,
  IonSegment, IonSegmentButton, IonLabel,
} from '@ionic/react';
import { add, grid, list, imageOutline, settingsOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useCards, useUpdateCard } from '../hooks/useCards';
import { usePreferredGames } from '../hooks/useGames';
import { useCreateEvent } from '../hooks/useInventory';
import { Card, CardFilters } from '../types';
import CardItem from '../components/CardItem';
import SearchFilterBar from '../components/SearchFilterBar';
import { API_BASE } from '../config/api';

const resolveImageUrl = (url?: string) =>
  url && url.startsWith('/uploads') ? `${API_BASE}${url}` : url;

const CardList: React.FC = () => {
  const [filters, setFilters] = useState<CardFilters>({ page: 1, per_page: 20 });
  const [viewMode, setViewMode] = useState<'list' | 'gallery'>('list');
  const { data, isLoading } = useCards(filters);
  const { data: games = [] } = usePreferredGames();
  const history = useHistory();
  const createEvent = useCreateEvent();
  const updateCard = useUpdateCard();
  const [presentAlert] = useIonAlert();

  const showSaleAlert = (card: Card) => {
    presentAlert({
      header: 'Mark as Sold',
      subHeader: card.name,
      message: `Record a sale for "${card.name}"?`,
      inputs: [
        { name: 'salePrice', type: 'number', placeholder: 'Sale price', min: 0 },
        { name: 'notes', type: 'text', placeholder: 'Notes (optional)' },
      ],
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm Sale',
          handler: (data) => {
            const price = parseFloat(data.salePrice);
            createEvent.mutate({
              event_type: 'sale',
              card_id: card.id,
              card_name: card.name,
              game_name: card.game?.name || 'Unknown',
              quantity: 1,
              value: card.estimated_value,
              sale_price: isNaN(price) ? undefined : price,
              notes: data.notes || undefined,
            });
          },
        },
      ],
    });
  };

  const showLostAlert = (card: Card) => {
    presentAlert({
      header: 'Mark as Lost',
      subHeader: card.name,
      message: `Are you sure you want to mark "${card.name}" as lost?`,
      inputs: [
        { name: 'notes', type: 'text', placeholder: 'Notes (optional)' },
      ],
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm Lost',
          cssClass: 'alert-button-danger',
          handler: (data) => {
            createEvent.mutate({
              event_type: 'loss',
              card_id: card.id,
              card_name: card.name,
              game_name: card.game?.name || 'Unknown',
              quantity: 1,
              value: card.estimated_value,
              notes: data.notes || undefined,
            });
          },
        },
      ],
    });
  };

  const handleAddQuantity = (card: Card) => {
    presentAlert({
      header: 'Add Quantity',
      subHeader: card.name,
      message: `Current quantity: ${card.quantity}`,
      inputs: [
        { name: 'amount', type: 'number', placeholder: 'Amount to add', min: 1, value: 1 },
      ],
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Add',
          handler: (data) => {
            const amount = parseInt(data.amount, 10);
            if (!amount || amount < 1) return;
            updateCard.mutate({ id: card.id, data: { quantity: card.quantity + amount } });
          },
        },
      ],
    });
  };

  const handleTradeOut = (card: Card) => {
    history.push(`/trades/new?cardOut=${card.id}`);
  };

  const handleFilterChange = (newFilters: Partial<CardFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const loadMore = async (e: CustomEvent) => {
    if (data && filters.page! < data.pages) {
      setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }));
    }
    (e.target as HTMLIonInfiniteScrollElement).complete();
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary" style={{ position: 'relative' }}>
          <IonTitle>My Cards</IonTitle>
          <IonIcon
            icon={settingsOutline}
            onClick={() => history.push('/settings')}
            style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 24, cursor: 'pointer', color: '#fff', zIndex: 10 }}
          />
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <SearchFilterBar filters={filters} games={games} onFilterChange={handleFilterChange} />

        <IonSegment
          value={viewMode}
          onIonChange={(e) => setViewMode(e.detail.value as 'list' | 'gallery')}
          style={{ margin: '0 16px 8px' }}
        >
          <IonSegmentButton value="list">
            <IonIcon icon={list} />
            <IonLabel>List</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="gallery">
            <IonIcon icon={grid} />
            <IonLabel>Gallery</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {isLoading ? (
          <div className="ion-text-center ion-padding"><IonSpinner /></div>
        ) : (
          <>
            {viewMode === 'list' ? (
              <IonList>
                {data?.items.map((card) => (
                  <CardItem
                    key={card.id}
                    card={card}
                    onClick={() => history.push(`/cards/${card.id}/edit`)}
                    onAddQuantity={handleAddQuantity}
                    onTradeOut={handleTradeOut}
                    onSale={showSaleAlert}
                    onLost={showLostAlert}
                  />
                ))}
                {data?.items.length === 0 && (
                  <div className="ion-text-center ion-padding">No cards found. Tap + to add your first card!</div>
                )}
              </IonList>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
                padding: '8px 12px',
              }}>
                {data?.items.map((card) => {
                  const imgSrc = resolveImageUrl(card.image_front_url);
                  return (
                    <div
                      key={card.id}
                      onClick={() => history.push(`/cards/${card.id}/edit`)}
                      style={{
                        cursor: 'pointer',
                        borderRadius: 8,
                        overflow: 'hidden',
                        background: 'var(--ion-card-background, var(--ion-item-background, #fff))',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                      }}
                    >
                      <div style={{
                        width: '100%',
                        aspectRatio: '2.5 / 3.5',
                        background: 'var(--ion-color-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}>
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={card.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <IonIcon icon={imageOutline} style={{ fontSize: 48, color: 'var(--ion-color-medium)' }} />
                        )}
                      </div>
                      <div style={{ padding: '6px 8px' }}>
                        <div style={{
                          fontWeight: 600, fontSize: 13,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {card.name}
                        </div>
                        <div style={{
                          fontSize: 12, color: 'var(--ion-color-medium)',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {card.set_name || card.game?.name || ''}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--ion-color-medium)', marginTop: 2 }}>
                          Qty: {card.quantity}
                          {(card.estimated_value != null || card.purchase_price != null) && ' ('}
                          {card.estimated_value != null && `$${card.estimated_value.toFixed(2)}`}
                          {card.estimated_value != null && card.purchase_price != null && ' / '}
                          {card.purchase_price != null && `$${card.purchase_price.toFixed(2)}`}
                          {(card.estimated_value != null || card.purchase_price != null) && ')'}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {data?.items.length === 0 && (
                  <div className="ion-text-center ion-padding" style={{ gridColumn: '1 / -1' }}>
                    No cards found. Tap + to add your first card!
                  </div>
                )}
              </div>
            )}
            <IonInfiniteScroll onIonInfinite={loadMore} disabled={!data || filters.page! >= data.pages}>
              <IonInfiniteScrollContent />
            </IonInfiniteScroll>
          </>
        )}

        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={() => history.push('/cards/new/edit')}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>

      </IonContent>
    </IonPage>
  );
};

export default CardList;
